/**
 * PlatformLayoutRoute — platform-admin shell (manage Professionals (CA firms)).
 * @file src/web/features/platform/components/PlatformLayoutRoute.tsx
 *
 * Sidebar layout for the platform operator (admin.system). Separate from
 * the CA-facing AdminLayoutRoute — different nav, a "Platform" badge, and
 * a stricter role gate.
 */

import { Suspense, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Building2, LogOut, Menu, X, PanelLeftClose, PanelLeftOpen, Settings } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AuthGuard } from '../../auth';
import { useAuth } from '../../auth/hooks';
import { brand } from '../../../shared/brand';

const NAV: { label: string; href: string; icon: LucideIcon }[] = [
  { label: 'Professionals', href: '/platform/professionals', icon: Building2 },
  { label: 'Settings', href: '/platform/settings', icon: Settings },
];

function Sidebar({
  onNavigate,
  collapsed,
  onToggleCollapse,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const active = (h: string) => loc.pathname === h || loc.pathname.startsWith(h + '/');
  const initials = (user?.name || user?.email || 'od').slice(0, 2).toUpperCase();
  return (
    <div className="flex h-full flex-col">
      <div className={`py-5 ${collapsed ? 'flex justify-center px-0' : 'px-5'}`}>
        {collapsed ? (
          <div className="flex flex-col items-center">
            <Link to="/platform/professionals" onClick={onNavigate} className="inline-block" title="Go to Professionals">
              <img src={brand.icon} alt="oddits" className="h-8 w-8" />
            </Link>
            <span className="mt-2 size-2 rounded-full bg-inkblack" title="Platform" />
          </div>
        ) : (
          <>
            <Link to="/platform/professionals" onClick={onNavigate} className="inline-block" title="Go to Professionals">
              <img src={brand.logo} alt="oddits" className="h-11 w-auto" />
            </Link>
            <span className="mt-2.5 inline-block rounded-full bg-highlighter-40 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-inkblack">Platform</span>
          </>
        )}
      </div>
      <nav className="flex-1 space-y-1 px-3 py-2">
        {NAV.map((n) => (
          <NavLink
            key={n.href}
            to={n.href}
            onClick={onNavigate}
            title={collapsed ? n.label : undefined}
            className={`flex items-center rounded-xl text-sm font-medium no-underline transition-colors ${
              collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
            } ${active(n.href) ? 'bg-highlighter-40 text-inkblack' : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900'}`}
          >
            <n.icon className={`h-4.5 w-4.5 shrink-0 ${active(n.href) ? 'text-inkblack' : 'text-ink-400'}`} />
            {!collapsed && n.label}
          </NavLink>
        ))}
      </nav>
      {onToggleCollapse && (
        <div className="px-3 pb-1">
          <button
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`hidden w-full items-center rounded-xl py-2.5 text-sm font-medium text-ink-500 transition-colors hover:bg-ink-50 hover:text-ink-900 md:flex ${
              collapsed ? 'justify-center px-0' : 'gap-3 px-3'
            }`}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4.5 w-4.5 shrink-0 text-ink-400" />
            ) : (
              <>
                <PanelLeftClose className="h-4.5 w-4.5 shrink-0 text-ink-400" />
                Collapse
              </>
            )}
          </button>
        </div>
      )}
      <div className="border-t border-ink-100 p-3">
        {collapsed ? (
          <div className="flex justify-center py-2" title={`${user?.name || 'Platform admin'} · ${user?.email ?? ''}`}>
            <span className="grid size-9 place-items-center rounded-lg bg-gradient-to-br from-ink-700 to-ink-900 text-xs font-semibold text-white">
              {initials}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-xl px-2 py-2">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-ink-700 to-ink-900 text-xs font-semibold text-white">
              {initials}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink-900">{user?.name || 'Platform admin'}</p>
              <p className="truncate text-xs text-ink-500">{user?.email}</p>
            </div>
          </div>
        )}
        <button
          onClick={() => logout()}
          title={collapsed ? 'Sign out' : undefined}
          className={`mt-1 flex w-full items-center rounded-xl py-2.5 text-sm font-medium text-ink-600 transition-colors hover:bg-danger-50 hover:text-danger-600 ${
            collapsed ? 'justify-center px-0' : 'gap-3 px-3'
          }`}
        >
          <LogOut className="h-4.5 w-4.5 shrink-0 text-ink-400" /> {!collapsed && 'Sign out'}
        </button>
      </div>
    </div>
  );
}

export function PlatformLayoutRoute() {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('odd:sidebar') === 'collapsed',
  );
  const toggleCollapse = () =>
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('odd:sidebar', next ? 'collapsed' : 'expanded'); } catch { /* ignore */ }
      return next;
    });
  return (
    <AuthGuard requiredRoles={['moderator.manage', 'admin.system']}>
      <div className="min-h-screen bg-ink-50 text-ink-900 antialiased">
        <aside
          className={`fixed inset-y-0 left-0 z-30 hidden border-r border-ink-200 bg-white transition-[width] duration-200 md:block ${
            collapsed ? 'w-16' : 'w-64'
          }`}
        >
          <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
        </aside>
        {open && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-ink-900/40" onClick={() => setOpen(false)} />
            <aside className="absolute inset-y-0 left-0 w-64 border-r border-ink-200 bg-white shadow-xl">
              <button
                onClick={() => setOpen(false)}
                className="absolute right-3 top-4 text-ink-400 hover:text-ink-700"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
              <Sidebar onNavigate={() => setOpen(false)} />
            </aside>
          </div>
        )}
        <div className={`transition-[padding] duration-200 ${collapsed ? 'md:pl-16' : 'md:pl-64'}`}>
          {/* mobile top bar (hamburger + logo) — flows above content, no overlap */}
          <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-ink-200 bg-white/90 px-3 backdrop-blur md:hidden">
            <button
              onClick={() => setOpen(true)}
              className="grid size-10 place-items-center rounded-xl text-ink-600 transition-colors hover:bg-ink-100"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/platform/professionals" className="inline-block" title="Go to Professionals">
              <img src={brand.logo} alt="oddits" className="h-7 w-auto" />
            </Link>
          </header>
          <main className="mx-auto max-w-7xl px-6 py-8">
            <Suspense fallback={<div className="flex min-h-[40vh] items-center justify-center"><div className="size-6 animate-spin rounded-full border-2 border-ink-200 border-t-brand-600" /></div>}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    </AuthGuard>
  );
}
